import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { workforceApi } from "./workforceApi";

/**
 * EmployeeDialog — create OR edit a workforce_employee.
 *
 * Product Owner directive: Employee is the primary business entity;
 * an application login is optional. This dialog therefore allows
 * empty email (contract drivers, service techs) but still validates
 * work_group against the backend enum.
 */
export default function EmployeeDialog({ open, onClose, onSaved, editing, workGroups, readOnly }) {
  const [form, setForm] = useState(defaultForm(editing));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(defaultForm(editing)); }, [editing, open]);
  if (!open) return null;

  const save = async () => {
    if (!form.full_name.trim()) return toast.error("Full name is required");
    if (form.birthday_mm_dd && !/^\d{2}-\d{2}$/.test(form.birthday_mm_dd)) {
      return toast.error("Birthday must be in MM-DD format (e.g. 06-11)");
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        email: form.email.trim() || null,
        preferred_name: form.preferred_name.trim() || null,
        birthday_mm_dd: form.birthday_mm_dd.trim() || null,
        employee_number:
          form.employee_number === "" || form.employee_number === null || form.employee_number === undefined
            ? null
            : parseInt(form.employee_number, 10),
      };
      if (editing) {
        await workforceApi.updateEmp(editing.id, body);
        toast.success("Employee updated");
      } else {
        await workforceApi.createEmp(body);
        toast.success("Employee created");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="workforce-employee-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Employee</div>
            <div className="mt-0.5 font-display text-lg font-semibold text-slate-900">
              {editing ? "Edit Employee" : "New Employee"}
            </div>
          </div>
          <button
            data-testid="workforce-employee-close"
            onClick={onClose}
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Full Name *" span2>
            <input
              data-testid="workforce-emp-fullname"
              disabled={readOnly}
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Preferred Name (nickname)" span2>
            <input
              data-testid="workforce-emp-preferred-name"
              disabled={readOnly}
              value={form.preferred_name}
              onChange={(e) => setForm({ ...form, preferred_name: e.target.value })}
              placeholder='e.g. "Dick" for Richard — leave blank to use full name'
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Email (optional)">
            <input
              data-testid="workforce-emp-email"
              disabled={readOnly}
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Leave blank if no login"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
            {editing?.user_id && (
              <div className="mt-1 text-[11px] text-emerald-700" data-testid="workforce-emp-userlink">
                Linked to user account
              </div>
            )}
          </Field>
          <Field label="Employee Number">
            <input
              data-testid="workforce-emp-number"
              disabled={readOnly}
              type="number"
              min={0}
              step={1}
              value={form.employee_number}
              onChange={(e) => setForm({ ...form, employee_number: e.target.value })}
              placeholder="Payroll #"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Birthday (MM-DD)">
            <input
              data-testid="workforce-emp-birthday"
              disabled={readOnly}
              value={form.birthday_mm_dd}
              onChange={(e) => setForm({ ...form, birthday_mm_dd: e.target.value })}
              placeholder="MM-DD (e.g. 06-11)"
              pattern="\d{2}-\d{2}"
              maxLength={5}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
            />
            <div className="mt-1 text-[11px] text-slate-500">
              Month &amp; day only. Birth year is not stored.
            </div>
          </Field>
          <Field label="Role Title">
            <input
              data-testid="workforce-emp-role"
              disabled={readOnly}
              value={form.role_title}
              onChange={(e) => setForm({ ...form, role_title: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Work Group">
            <select
              data-testid="workforce-emp-workgroup"
              disabled={readOnly}
              value={form.work_group}
              onChange={(e) => setForm({ ...form, work_group: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">—</option>
              {workGroups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Department">
            <input
              data-testid="workforce-emp-department"
              disabled={readOnly}
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Hire Date">
            <input
              data-testid="workforce-emp-hiredate"
              disabled={readOnly}
              type="date"
              value={form.hire_date}
              onChange={(e) => setForm({ ...form, hire_date: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Vacation Entitlement (days / anniversary year)">
            <input
              data-testid="workforce-emp-entitlement"
              disabled={readOnly}
              type="number"
              min={0}
              step={0.5}
              value={form.vacation_entitlement_days}
              onChange={(e) => setForm({ ...form, vacation_entitlement_days: parseFloat(e.target.value || "0") })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Status">
            <select
              data-testid="workforce-emp-status"
              disabled={readOnly}
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </Field>
          <Field label="Phone Number">
            <input
              data-testid="workforce-emp-phone"
              disabled={readOnly}
              value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              placeholder="(519) 555-0142"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Personal Email">
            <input
              data-testid="workforce-emp-personal-email"
              disabled={readOnly}
              type="email"
              value={form.personal_email}
              onChange={(e) => setForm({ ...form, personal_email: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Address" span2>
            <input
              data-testid="workforce-emp-address"
              disabled={readOnly}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Emergency Contact — Name">
            <input
              data-testid="workforce-emp-ec-name"
              disabled={readOnly}
              value={form.emergency_contact_name}
              onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Emergency Contact — Phone">
            <input
              data-testid="workforce-emp-ec-phone"
              disabled={readOnly}
              value={form.emergency_contact_phone}
              onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Emergency Contact — Relationship" span2>
            <input
              data-testid="workforce-emp-ec-relationship"
              disabled={readOnly}
              value={form.emergency_contact_relationship}
              onChange={(e) => setForm({ ...form, emergency_contact_relationship: e.target.value })}
              placeholder="e.g. Spouse, Parent, Sibling"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Notes" span2>
            <textarea
              data-testid="workforce-emp-notes"
              disabled={readOnly}
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </Field>
        </div>

        {!readOnly && (
          <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            <button onClick={onClose} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button
              data-testid="workforce-employee-save"
              onClick={save}
              disabled={saving}
              className="rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-sm font-semibold text-ink-inverse hover:bg-slate-900 disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Employee"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, span2 }) {
  return (
    <label className={`block ${span2 ? "md:col-span-2" : ""}`}>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      {children}
    </label>
  );
}

function defaultForm(editing) {
  return {
    full_name: editing?.full_name || "",
    preferred_name: editing?.preferred_name || "",
    email: editing?.email || "",
    employee_number: editing?.employee_number ?? "",
    birthday_mm_dd: editing?.birthday_mm_dd || "",
    role_title: editing?.role_title || "",
    department: editing?.department || "",
    work_group: editing?.work_group || "",
    hire_date: editing?.hire_date || "",
    vacation_entitlement_days: editing?.vacation_entitlement_days ?? 0,
    status: editing?.status || "Active",
    notes: editing?.notes || "",
    phone_number: editing?.phone_number || "",
    personal_email: editing?.personal_email || "",
    address: editing?.address || "",
    emergency_contact_name: editing?.emergency_contact_name || "",
    emergency_contact_phone: editing?.emergency_contact_phone || "",
    emergency_contact_relationship: editing?.emergency_contact_relationship || "",
  };
}
